import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/classnames.js';
import { ButtonSparkles } from './ButtonSparkles.js';

/**
 * MagneticButton — kept for its call sites, but the magnetism is gone: it now
 * renders the shared MOLTEN GOLD button (`.btn-primary` / `.btn-secondary` in
 * globals.css) so every CTA across the app is the same living-gold treatment.
 *  - variant 'primary'    → the full molten gold face (hero CTA scale).
 *  - variant 'gold-ghost' → the molten-outline secondary (floods on hover).
 *
 * Polymorphic: pass `to` to render a router Link, otherwise a <button>.
 */
type Variant = 'primary' | 'gold-ghost';

interface BaseProps {
  children: ReactNode;
  variant?: Variant;
  className?: string;
  /** Retained for call-site compatibility; no longer used. */
  pull?: number;
  busy?: boolean;
}
type AsLink = BaseProps & { to: string; onClick?: () => void; disabled?: never };
type AsButton = BaseProps & {
  to?: undefined;
  onClick?: () => void;
  disabled?: boolean;
};

export function MagneticButton(props: AsLink | AsButton) {
  const { children, variant = 'primary', className, busy } = props;
  const cls = cn(
    variant === 'primary' ? 'btn-primary btn-cta' : 'btn-secondary',
    className,
  );
  const label = busy ? 'One moment…' : children;

  if (props.to !== undefined) {
    return (
      <Link to={props.to} onClick={props.onClick} className={cls} aria-busy={busy}>
        {label}
        <ButtonSparkles />
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled || busy}
      className={cls}
      aria-busy={busy}
    >
      {label}
      <ButtonSparkles />
    </button>
  );
}
